import { useState } from 'react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import {
  Menu,
  MenuItem,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
} from '@locus-taxy/encore-mui';
import { Logout, Person } from '@locus-taxy/encore-mui/icons';
import { useNavigate } from 'react-router-dom';
import { useLogoutWithO11y } from '../../hooks/useLogoutWithO11y';
import { usePreserveSearchParams } from '../../hooks/usePreserveSearchParams';
import { useClientId } from '../../hooks/useClientId';
import { buildClientPrefixedPath } from '../../utils/url';
import { useUser } from '../../hooks/useUser';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';

interface SidebarUserMenuProps {
  sidebarOpen: boolean;
}

export default function SidebarUserMenu({
  sidebarOpen,
}: Readonly<SidebarUserMenuProps>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { getUrlWithSearchParams } = usePreserveSearchParams();
  const clientId = useClientId();
  const logout = useLogoutWithO11y();
  const menuOpen = Boolean(anchorEl);
  const { user } = useUser();
  const userInitial = (
    user?.name?.[0] ??
    user?.email?.[0] ??
    'U'
  ).toUpperCase();

  const handleUserProfileClick = () => {
    setAnchorEl(null);
    // Navigate to account page with preserved search parameters
    const accountUrl = getUrlWithSearchParams(
      buildClientPrefixedPath('/account', clientId)
    );
    navigate(accountUrl);
  };

  const handleSignOutClick = () => {
    setAnchorEl(null);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <>
      <ListItem disablePadding>
        <Tooltip
          title={t('sidebar.account')}
          placement="right"
          disableHoverListener={sidebarOpen}
        >
          <ListItemButton
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{ color: 'text.secondary' }}
            data-testid={getTestId({
              action: TestIdAction.OPEN,
              object: 'button',
              entity: 'user-menu',
              page: 'sidebar',
            })}
          >
            <ListItemIcon>
              <Avatar sx={{ width: 24, height: 24 }}>{userInitial}</Avatar>
            </ListItemIcon>
            {sidebarOpen && <ListItemText primary={t('sidebar.profile')} />}
          </ListItemButton>
        </Tooltip>
      </ListItem>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            minWidth: 140,
            fontSize: 12,

            '.MuiButtonBase-root.MuiMenuItem-root': {
              fontSize: 12,
            },
          },
        }}
      >
        <MenuItem
          onClick={handleUserProfileClick}
          data-testid={getTestId({
            action: TestIdAction.NAVIGATE,
            object: 'list-item',
            entity: 'user-profile',
            page: 'sidebar',
          })}
        >
          <ListItemIcon>
            <Person fontSize="small" color="action" />
          </ListItemIcon>
          {t('sidebar.userProfile')}
        </MenuItem>
        <MenuItem onClick={handleSignOutClick}>
          <ListItemIcon>
            <Logout
              fontSize="small"
              color="action"
              data-testid={getTestId({
                action: TestIdAction.LOGOUT,
                object: 'list-item',
                entity: 'sign-out',
                page: 'sidebar',
              })}
            />
          </ListItemIcon>
          {t('sidebar.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
}
